import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { StatCard } from '@/components/common/StatCard';
import { PageHeader } from '@/components/common/PageHeader';
import { toast } from '@/lib/toast';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { ROUTES } from '@/lib/constants/routes';
import { useEntity } from '@/hooks/useEntity';
import {
  useDeactivateOffer,
  usePartnerOffers,
  usePublishOffer,
  useWithdrawOffer,
} from './hooks/usePartnerOffers';
import { OffersSection } from './components/OffersSection';
import {
  AcceptedOfferRow,
  AwaitingOfferRow,
  LiveOfferRow,
  RejectedOfferRow,
} from './components/OfferRows';
import { AllOffersTable } from './components/AllOffersTable';
import { PARTNER_OFFERS_STRINGS as S } from './partner-offers.constants';

/** Tinted icon chip behind each stat card's glyph. */
function StatIcon({ tint, children }: { tint: string; children: React.ReactNode }) {
  return (
    <span aria-hidden="true" className={`flex h-9 w-9 items-center justify-center rounded-[0.5625rem] ${tint}`}>
      {children}
    </span>
  );
}

/** Perks & Offers console (FR-29, MYN-104): the Trusted Partner's own self-service offer-management view. */
export function PartnerOffersConsolePage() {
  const entity = useEntity();
  const entityId = entity?.entityId ?? '';
  const offersQuery = usePartnerOffers(entityId);
  const withdraw = useWithdrawOffer(entityId);
  const publish = usePublishOffer(entityId);
  const deactivate = useDeactivateOffer(entityId);

  const handleWithdraw = (offerId: string) => {
    withdraw.mutate(offerId, {
      onSuccess: () => toast.success(S.WITHDRAW_SUCCESS),
      onError: (error) => toast.error(getApiErrorMessage(error, S.WITHDRAW_ERROR)),
    });
  };
  const handlePublish = (offerId: string) => {
    publish.mutate(offerId, {
      onSuccess: () => toast.success(S.PUBLISH_SUCCESS),
      onError: (error) => toast.error(getApiErrorMessage(error, S.PUBLISH_ERROR)),
    });
  };
  const handleDeactivate = (offerId: string) => {
    deactivate.mutate(offerId, {
      onSuccess: () => toast.success(S.DEACTIVATE_SUCCESS),
      onError: (error) => toast.error(getApiErrorMessage(error, S.DEACTIVATE_ERROR)),
    });
  };

  return (
    <div className="min-h-full bg-page-bg">
      <PageHeader
        title={S.PAGE_TITLE}
        rightSlot={
          <Link to={ROUTES.PARTNER_OFFER_NEW}>
            <Button type="button" variant="green">
              {S.CREATE_NEW_OFFER}
            </Button>
          </Link>
        }
      />
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-10">
        <p className="text-p6 text-slate">{S.PAGE_DESCRIPTION}</p>

        {offersQuery.isPending && (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" label="Loading your offers" />
          </div>
        )}

        {offersQuery.isError && (
          <ErrorState
            title={S.LOAD_ERROR_TITLE}
            description={S.LOAD_ERROR_DESCRIPTION}
            onRetry={() => void offersQuery.refetch()}
          />
        )}

        {offersQuery.isSuccess && (
          <>
            {(() => {
              const offers = offersQuery.data;
              const awaiting = offers.filter((o) => o.status === 'submitted');
              const accepted = offers.filter((o) => o.status === 'accepted');
              const live = offers.filter((o) => o.status === 'live');
              const rejected = offers.filter((o) => o.status === 'rejected');

              return (
                <>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <StatCard
                      tinted={false}
                      icon={<StatIcon tint="bg-green/10 text-green">📄</StatIcon>}
                      label={S.STAT_TOTAL}
                      value={offers.length}
                    />
                    <StatCard
                      tinted={false}
                      icon={<StatIcon tint="bg-yellow/10 text-yellow">🕐</StatIcon>}
                      label={S.STAT_AWAITING}
                      value={awaiting.length}
                    />
                    <StatCard
                      tinted={false}
                      icon={<StatIcon tint="bg-green/10 text-green">✓</StatIcon>}
                      label={S.STAT_ACCEPTED}
                      value={accepted.length}
                    />
                    <StatCard
                      tinted={false}
                      icon={<StatIcon tint="bg-teal2/10 text-teal2">🌐</StatIcon>}
                      label={S.STAT_LIVE}
                      value={live.length}
                    />
                  </div>

                  <OffersSection
                    title={S.SECTION_AWAITING_TITLE}
                    description={S.SECTION_AWAITING_DESCRIPTION}
                    count={awaiting.length}
                    badgeVariant="warning"
                    isEmpty={awaiting.length === 0}
                  >
                    {awaiting.map((offer) => (
                      <AwaitingOfferRow
                        key={offer.id}
                        offer={offer}
                        onWithdraw={handleWithdraw}
                        isWithdrawing={withdraw.isPending}
                      />
                    ))}
                  </OffersSection>

                  {/* MYN-106 AC6. Rendered only when there is something to
                      show, unlike the three sections above: those are the
                      lifecycle a partner is always working through, whereas a
                      permanently-empty "Needs Changes" panel implies a
                      rejection is expected. */}
                  {rejected.length > 0 && (
                    <OffersSection
                      title={S.SECTION_REJECTED_TITLE}
                      description={S.SECTION_REJECTED_DESCRIPTION}
                      count={rejected.length}
                      badgeVariant="danger"
                      isEmpty={false}
                    >
                      {rejected.map((offer) => (
                        <RejectedOfferRow key={offer.id} offer={offer} />
                      ))}
                    </OffersSection>
                  )}

                  <OffersSection
                    title={S.SECTION_ACCEPTED_TITLE}
                    description={S.SECTION_ACCEPTED_DESCRIPTION}
                    count={accepted.length}
                    badgeVariant="success"
                    isEmpty={accepted.length === 0}
                  >
                    {accepted.map((offer) => (
                      <AcceptedOfferRow
                        key={offer.id}
                        offer={offer}
                        onPublish={handlePublish}
                        isPublishing={publish.isPending}
                      />
                    ))}
                  </OffersSection>

                  <OffersSection
                    title={S.SECTION_LIVE_TITLE}
                    description={S.SECTION_LIVE_DESCRIPTION}
                    count={live.length}
                    badgeVariant="teal"
                    isEmpty={live.length === 0}
                  >
                    {live.map((offer) => (
                      <LiveOfferRow
                        key={offer.id}
                        offer={offer}
                        onDeactivate={handleDeactivate}
                        isDeactivating={deactivate.isPending}
                      />
                    ))}
                  </OffersSection>

                  <OffersSection
                    title={S.SECTION_ALL_TITLE}
                    description={S.SECTION_ALL_DESCRIPTION}
                    count={offers.length}
                    badgeVariant="chip"
                    isEmpty={offers.length === 0}
                  >
                    <AllOffersTable offers={offers} />
                  </OffersSection>
                </>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}
